/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { ComponentType, ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import { FiArrowLeft } from 'react-icons/fi';
import { IoMdMenu } from 'react-icons/io';
import { useGetMe } from 'legacy/user/hooks';
import { throttle } from 'lodash';

// TODO: This component needs to be a function of the global application header.
// TODO: Component is bad, needs to be better at handling variants. Sadly no time for this right now.
// As it is, this component has knowledge of its placement and how it should adjust itself on the page.
// This component should not have any notion of how it should be used.
// Unfortunately because of the way the app is built, there is no workaround unless we re-do the overall app frame... One day...

export enum TopBarVariants {
  Regular = 'regular',
  Big = 'big'
}

interface Props {
  title: string;
  icon?: string;
  subtitle?: string;
  onBack?: () => void;
  onMenuClick?: () => void;
  scrollAnimated?: boolean;
  variant?: TopBarVariants;
  BackIcon?: ComponentType;
  children?: ReactNode;
  wideContent?: boolean;
  gradientBackground?: boolean;
}

const ScreenTopBar: React.FC<Props> = ({
  onBack,
  onMenuClick,
  title,
  children,
  scrollAnimated,
  subtitle,
  variant = TopBarVariants.Regular,
  icon,
  BackIcon = FiArrowLeft,
  wideContent,
  gradientBackground
}) => {
  const [isScrolled, setIsScrolled] = useState(window.scrollY > 0);
  const { data: user } = useGetMe();

  const onScrollHandler = throttle(() => {
    if (scrollAnimated) {
      setIsScrolled(() => window.scrollY > 40);
    }
  }, 200);

  useEffect(() => {
    window.addEventListener('scroll', onScrollHandler);
    return () => window.removeEventListener('scroll', onScrollHandler);
  }, []);

  return (
    <div
      className={classNames(
        'fixed h-14 top-0 lg:top-[50px] left-0 w-full px-4 py-2 md:py-6 z-50 flex items-center transition-all duration-200 transition-ease-linear',
        {
          'md:top-[80px]': !!user
        },
        (!scrollAnimated || isScrolled) &&
          (!gradientBackground || isScrolled) &&
          'bg-white shadow-sm',
        variant === TopBarVariants.Big ? 'md:h-20' : 'md:h-16'
      )}
      style={
        gradientBackground && !isScrolled
          ? {
              background:
                'linear-gradient(180deg, rgba(11, 11, 11, 0.90) -83.66%, rgba(0, 0, 0, 0.00) 100%)'
            }
          : undefined
      }
    >
      <div
        className={classNames(
          'w-full flex md:mx-auto',
          wideContent ? 'md:max-w-[1200px]' : 'md:w-[800px]'
        )}
      >
        <div className="flex flex-1 overflow-hidden">
          {onBack && BackIcon && (
            <button
              type="button"
              className="mr-2 relative z-10"
              onClick={onBack}
            >
              <BackIcon
                size={24}
                className={classNames(
                  'transition-all',
                  !scrollAnimated || isScrolled ? 'text-gray-400' : 'text-white'
                )}
              />
            </button>
          )}

          <div className="w-full flex overflow-hidden">
            <div
              className={classNames(
                'flex md:justify-center transition-all overflow-hidden flex-1',
                !scrollAnimated || isScrolled
                  ? 'text-neutral-600'
                  : 'text-white',
                icon && isScrolled && scrollAnimated && 'translate-x-[0]',
                icon &&
                  scrollAnimated &&
                  !isScrolled &&
                  'translate-x-[-40px] md:translate-x-[-74px]'
              )}
            >
              {icon && (
                <img
                  src={icon}
                  alt="Header badge"
                  className={classNames(
                    'w-[32px] h-[32px] md:w-[48px] md:h-[48px] rounded-xl md:ml-4 mr-2 md:mr-4 transition-all pointer-events-none self-center',
                    scrollAnimated && !isScrolled && 'opacity-0',
                    scrollAnimated && isScrolled && 'opacity-100'
                  )}
                />
              )}
              <div className="flex flex-col justify-center overflow-hidden flex-1">
                <h1
                  className={classNames(
                    'text-base md:text-xl md:font-semibold md:mr-4 transition-all',
                    !scrollAnimated || isScrolled
                      ? 'text-neutral-900'
                      : 'text-white'
                  )}
                >
                  {title}
                </h1>
                {subtitle && (
                  <div
                    className="text-xs md:text-base font-normal whitespace-nowrap overflow-hidden text-ellipsis"
                    style={{ maxWidth: 'calc(100% - 40px)' }}
                  >
                    {subtitle}
                  </div>
                )}
              </div>
            </div>

            {(scrollAnimated && isScrolled) || !scrollAnimated
              ? children
              : null}
          </div>
        </div>
        <div className="flex items-center">
          {onMenuClick && (
            <button type="button" onClick={onMenuClick}>
              <IoMdMenu
                size={24}
                className={classNames(
                  'transition-all',
                  !scrollAnimated || isScrolled
                    ? 'text-primary-500'
                    : 'text-white'
                )}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreenTopBar;
